import { CSDataType, CSSubDataType } from '../task-list-main'
import { t } from '@/i18n'

const KindMap = {
  TaskKindImmediately: t('立即'),
  TaskKindBooking: t('预定'),
  TaskKindCycle: t('周期'),
}

const statusMap = {
  TaskStatusFinished: { label: t('已完成'), className: 'success' },
  TaskStatusRunning: { label: t('进行中'), className: 'processing' },
  TaskStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskStatusError: { label: t('错误'), className: 'error' },
  TaskStatusPausing: { label: t('停止中'), className: 'warning' },
  TaskStatusPause: { label: t('暂停'), className: 'warning' },
}

const statusResultMap = {
  TaskResultStatusFinished: { label: t('已完成'), className: 'success' },
  TaskResultStatusProcessing: { label: t('进行中'), className: 'processing' },
  TaskResultStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskResultStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskResultStatusStopping: { label: t('停止中'), className: 'warning' },
}

// 计算风险等级
const getRiskLevel = (score: number, isUnique: boolean) => {
  console.log('score', score)
  if (isUnique) return '-'
  if (score <= 100 && score >= 80) return t('优秀')
  if (score < 80 && score >= 60) return t('良好')
  if (score < 60 && score >= 40) return t('一般')
  if (score < 40 && score >= 20) return t('较弱')
  return t('弱')
}

// 计算风险等级样式
const getRiskClassName = (score: number, isUnique: boolean) => {
  console.log('score2', score)
  if (isUnique) return 'serious-risk'
  if (score <= 100 && score >= 80) return 'low-risk'
  if (score < 80 && score >= 60) return 'medium-risk'
  if (score < 60 && score >= 40) return 'high-risk'
  if (score < 40 && score >= 20) return 'weaker-risk'
  return 'serious-risk'
}

// 处理单个任务项
const processTaskItem = (item: any, taskId: string, subRowKeys: string[]) => {
  const { firstExecuteTime, latestExecuteTime, id, kind, statusList, taskStatus, exceptReason } =
    item

  const isUnique = !statusList.some(status => status.status !== 'TaskResultStatusInterrupted')
  const score = (
    item.score === 0 ? (isUnique ? '-' : 0) : item.score === 10000 ? 100 : item.score / 100 || 0
  ) as number

  item.firstExecuteTime = firstExecuteTime
    ? new Date(firstExecuteTime).toLocaleString().replace(/\//g, '-')
    : '-'
  item.latestExecuteTime = latestExecuteTime
    ? new Date(latestExecuteTime).toLocaleString().replace(/\//g, '-')
    : '-'
  item.key = id
  item.kind = KindMap[kind]
  item.score = {
    score,
    riskLevel: getRiskLevel(score, isUnique),
    className: getRiskClassName(score, isUnique),
  }
  item.statusList = statusList.map(status => ({
    name: statusResultMap[status.status].label,
    count: status.count,
    className: statusResultMap[status.status].className,
  }))
  item.isRemove = !statusList.find(status =>
    ['TaskResultStatusWaiting', 'TaskResultStatusProcessing', 'TaskResultStatusStopping'].includes(
      status.status,
    ),
  )

  item.taskStatus =
    kind === 'TaskKindCycle'
      ? statusMap[taskStatus]?.label !== t('已完成')
        ? statusMap[taskStatus]?.label
        : undefined
      : undefined
  item.exceptReason = exceptReason ? exceptReason : undefined

  if (item.id === taskId) {
    item.subRowKeys = subRowKeys
  }

  return item
}

// 构建主任务数据
export const buildDataSource = (
  data: any[],
  taskId: string,
  subRowKeys: string[],
  isOrigin?: boolean,
): CSDataType[] => {
  if (!data || data.length === 0) return []

  const arr = JSON.parse(JSON.stringify(data))
  const tableData: CSDataType[] = []

  // 仅是更新 subRowKeys
  if (isOrigin) {
    for (let i = 0; i < arr.length; i++) {
      let item = arr[i]
      if (item.id === taskId) {
        item.subRowKeys = subRowKeys
      }
      tableData.push(item)
    }
    return tableData
  }

  for (let i = 0; i < arr.length; i++) {
    const processedItem = processTaskItem(arr[i], taskId, subRowKeys)
    tableData.push(processedItem)
  }

  return tableData
}

// 处理单个子任务项
const processSubTaskItem = (item: any, taskId: string) => {
  const { id, taskName, status, createTime, startTime, endTime, resourceSendTotal } = item
  const score = (
    item.score === 0
      ? status !== 'TaskResultStatusFinished'
        ? '-'
        : 0
      : (item.score / 100).toFixed(2) || 0
  ) as number

  item.key = id
  item.taskId = taskId
  item.taskName = taskName
  item.score = {
    score,
    riskLevel: getRiskLevel(score, status !== 'TaskResultStatusFinished'),
    className: getRiskClassName(score, status !== 'TaskResultStatusFinished'),
  }
  item.status = statusResultMap[status]
  item.createTime = new Date(createTime).toLocaleString().replace(/\//g, '-')
  item.startTime = startTime ? new Date(startTime).toLocaleString().replace(/\//g, '-') : '-'
  item.endTime = endTime ? new Date(endTime).toLocaleString().replace(/\//g, '-') : '-'

  const waitingStatuses = [
    'TaskResultStatusWaiting',
    'TaskResultStatusProcessing',
    'TaskResultStatusStopping',
  ]
  const processingStatuses = [
    'TaskResultStatusProcessing',
    'TaskResultStatusInterrupted',
    'TaskResultStatusStopping',
  ]
  const viewStatuses = [
    'TaskResultStatusWaiting',
    'TaskResultStatusProcessing',
    'TaskResultStatusInterrupted',
    'TaskResultStatusStopping',
  ]
  const stopStatuses = ['TaskResultStatusWaiting', 'TaskResultStatusProcessing']

  item.isReport = !(
    waitingStatuses.includes(status) ||
    (processingStatuses.includes(status) && resourceSendTotal === 0)
  )
  item.isRetest = !waitingStatuses.includes(status)
  item.isRemove = !waitingStatuses.includes(status)
  item.isView = !(viewStatuses.includes(status) && resourceSendTotal === 0)
  item.isStop = stopStatuses.includes(status)

  return item
}

// 构建子任务数据
export const buildSubDataSource = (data: any[], taskId: string): CSSubDataType[] => {
  if (!data || data.length === 0) return []

  const arr = JSON.parse(JSON.stringify(data))
  const subTableData: CSSubDataType[] = []

  for (let i = 0; i < arr.length; i++) {
    const processedItem = processSubTaskItem(arr[i], taskId)
    subTableData.push(processedItem)
  }

  return subTableData
}
